import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { useLeases } from '@/hooks/useLeases';
import { usePayments } from '@/hooks/usePayments';
import { useMaintenance } from '@/hooks/useMaintenance';
import { formatCurrency, formatDate } from '@/utils/format';

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: leases, isLoading } = useLeases();

  const activeLease = leases?.find((l) => l.status === 'active') ?? null;
  // signing_status=pending_tenant -- договор ждёт подписи именно арендатором.
  const pendingLeases =
    leases?.filter((l) => l.signing_status === 'pending_tenant') ?? [];
  // Подписан, но ещё не активен (напр. будущая дата старта / лаг активации) --
  // чтобы арендатор не видел «нет аренды» сразу после подписания.
  const signedNotActive =
    leases?.filter((l) => l.signing_status === 'fully_signed' && l.status !== 'active') ?? [];

  const payments = usePayments(activeLease ? { lease_id: activeLease.id } : {});
  const maintenance = useMaintenance(activeLease?.id ?? null);

  if (isLoading) return <Spinner />;

  // Ближайший неоплаченный = самый РАННИЙ по due_date. Бэкенд отдаёт платежи
  // orderByDesc(due_date), поэтому простой find() брал самый ПОЗДНИЙ (напр. Июнь
  // 2027) -- из-за этого оплата/чек уходили не за тот месяц.
  const nextPayment = [...(payments.data?.data ?? [])]
    .filter((p) => p.status !== 'paid' && p.status !== 'cancelled')
    .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];

  return (
    <div className="space-y-4">
      {/* Договоры на подписание — приоритетно, чтобы арендатор не застрял на
          «нет активной аренды», когда есть договор, который надо подписать. */}
      {pendingLeases.length > 0 && (
        <Card title={t('dashboard.pending_leases')}>
          <ul className="space-y-3">
            {pendingLeases.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {l.property?.address ?? t('leases.property_removed')}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatDate(l.start_date)} — {formatDate(l.end_date)} ·{' '}
                    {formatCurrency(l.monthly_rent)} / {t('common.month')}
                  </p>
                </div>
                <Link
                  to={`/leases/${l.id}/sign`}
                  className="shrink-0 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white hover:opacity-90"
                >
                  {t('leases.sign.cta')}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {signedNotActive.length > 0 && (
        <Card title={t('dashboard.signed_not_active')}>
          <ul className="space-y-3">
            {signedNotActive.map((l) => (
              <li
                key={l.id}
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
              >
                <p className="truncate text-sm font-medium text-slate-800">
                  {l.property?.address ?? t('leases.property_removed')}
                </p>
                <p className="text-xs text-slate-500">
                  {t('dashboard.starts_on', { date: formatDate(l.start_date) })}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {activeLease ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title={t('dashboard.my_home')}>
            <p className="text-sm text-slate-700">{activeLease.property?.address}</p>
            <p className="mt-1 text-xs text-slate-500">
              {formatDate(activeLease.start_date)} — {formatDate(activeLease.end_date)}
            </p>
            <p className="mt-2 text-sm font-medium">
              {formatCurrency(activeLease.monthly_rent)} / {t('common.month')}
            </p>
          </Card>

          <Card title={t('dashboard.next_payment')}>
            {nextPayment ? (
              <>
                <p className="text-2xl font-semibold text-slate-900">
                  {formatCurrency(nextPayment.amount_expected)}
                </p>
                <p className="text-xs text-slate-500">
                  {t('payments.due_by')}: {formatDate(nextPayment.due_date)}
                </p>
                <div className="mt-3">
                  <Badge tone={nextPayment.status === 'overdue' ? 'danger' : 'info'}>
                    {t(`payments.status.${nextPayment.status}`)}
                  </Badge>
                </div>
                <Link
                  to={`/payments/${nextPayment.id}`}
                  className="mt-3 inline-block text-sm font-medium text-brand hover:underline"
                >
                  {t('payments.pay_now')} →
                </Link>
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('web.empty.no_data')}</p>
            )}
          </Card>

          <Card title={t('maintenance.active_requests')} className="md:col-span-2">
            {maintenance.data && maintenance.data.length > 0 ? (
              <ul className="space-y-2">
                {maintenance.data.slice(0, 5).map((m) => (
                  <li key={m.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{m.title}</span>
                    <Badge tone="info">{t(`maintenance.status.${m.status}`)}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">{t('web.empty.no_data')}</p>
            )}
          </Card>
        </div>
      ) : pendingLeases.length === 0 && signedNotActive.length === 0 ? (
        <Card title={t('web.empty.no_active_lease')}>
          <p className="text-sm text-slate-600">
            <Link to="/search" className="text-brand hover:underline">
              {t('web.nav.search')}
            </Link>
          </p>
        </Card>
      ) : null}
    </div>
  );
}
